import type { AnyFlowSpec } from '@elara/core/flows';
import type { ClientPlatform } from '@elara/domain/client-platform';

import { errorMessage } from '@elara/core/errors';
import { CLIENT_PLATFORM_HEADER } from '@elara/domain/client-platform';

export interface FlowCallOptions {
  signal?: AbortSignal;
  /**
   * Let the request outlive the page (`fetch` `keepalive`), for a last report sent from `pagehide`.
   * Ignored by fetch implementations that don't support it.
   */
  keepalive?: boolean;
}

export interface FlowStreamOptions extends FlowCallOptions {}

export interface FlowResult<T> {
  data: T;
}

export interface FlowStreamResult<Output, Chunk> {
  data: Promise<Output>;
  stream: AsyncIterable<Chunk>;
}

export type AttachedFlow<Spec extends AnyFlowSpec> = FlowCallable<
  Spec['name'],
  Spec['InputType'],
  Spec['OutputType'],
  Spec['ChunkType']
>;

export interface FlowCallable<Name extends string, Input, Output, Chunk> {
  name: Name;
  (data: Input, options?: FlowCallOptions): Promise<FlowResult<Output>>;
  stream: (data: Input, options?: FlowStreamOptions) => Promise<FlowStreamResult<Output, Chunk>>;
}

export class FlowHttpError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Error code for a request that never reached the server because the device has no usable
 * network. Distinct from any server-issued code — the status is 0 because no response exists.
 */
export const OFFLINE_FLOW_ERROR_CODE = 'offline';

/** User-facing message for a flow that could not be attempted because the device is offline. */
export const OFFLINE_FLOW_ERROR_MESSAGE = 'You’re offline — this needs an internet connection.';

/**
 * Error code for a request that failed at the network level while the device reads as online
 * (ELARA-1146): a connection the OS tore down while the app was suspended, a Wi-Fi/cellular
 * handoff, a reset mid-request. Status 0 for the same reason as {@link OFFLINE_FLOW_ERROR_CODE}.
 */
export const UNREACHABLE_FLOW_ERROR_CODE = 'unreachable';

/** User-facing message for a network-level failure while the device is online. */
export const UNREACHABLE_FLOW_ERROR_MESSAGE = 'Couldn’t reach Elara — please try again.';

/**
 * Whether an error is the transport's offline signal.
 *
 * Elara is CQRS: every mutation is a server flow over HTTP, so offline a mutation cannot succeed
 * at all. `fetch` rejects for that case with a bare, platform-specific error
 * (`TypeError: Network request failed` on React Native) that carries no code and is
 * indistinguishable from any other failure — so {@link performFlowFetch} normalises it into a
 * `FlowHttpError` with {@link OFFLINE_FLOW_ERROR_CODE} and call sites branch on this guard.
 *
 * Only raised when the device's connectivity reads offline. The same rejection while connectivity
 * reads online is {@link isUnreachableFlowError} instead: telling an online user they are offline
 * is what ELARA-1146 fixed.
 */
export function isOfflineFlowError(error: unknown): error is FlowHttpError {
  return error instanceof FlowHttpError && error.code === OFFLINE_FLOW_ERROR_CODE;
}

/** Whether an error is the transport's "online, but the request failed in flight" signal. */
export function isUnreachableFlowError(error: unknown): error is FlowHttpError {
  return error instanceof FlowHttpError && error.code === UNREACHABLE_FLOW_ERROR_CODE;
}

/**
 * Describes a network-level flow failure for an error report, or returns `undefined` for any other
 * error. The raw platform rejection lives only in `details` — it is deliberately not the error's
 * `cause`, because `errorMessage()` appends a cause to the message and this message is shown to
 * the guest — so a reporter must lift it out explicitly to make it visible in triage.
 */
export function describeNetworkFlowError(error: unknown): Record<string, unknown> | undefined {
  if (!isOfflineFlowError(error) && !isUnreachableFlowError(error)) return;
  const details = typeof error.details === 'object' && error.details !== null ? error.details : {};
  return { code: error.code, ...details };
}

interface FlowErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

interface RouterEvent<Chunk = unknown, Output = unknown> {
  flow?: string;
  chunk?: Chunk;
  output?: Output;
  error?: { code?: string; message?: string; details?: unknown };
}

export interface HttpFlowTransportOptions {
  getAuthToken?: () => Promise<string | undefined>;
  baseUrl?: string;
  onUnauthenticatedError?: (error: FlowHttpError) => Promise<void> | void;
  /**
   * Fetch implementation used for every flow request. Defaults to the global `fetch`.
   *
   * On web the global `fetch` exposes a streaming `response.body`, which the SSE parser
   * consumes via `getReader()`. React Native's default `fetch` returns `null` for
   * `response.body`, silently killing token-streaming — RN consumers must inject a
   * streaming-capable implementation (e.g. `expo/fetch`) here.
   */
  fetchImpl?: typeof fetch;
  /**
   * Which app shell this transport belongs to, sent as the `x-elara-client-platform` header on
   * every flow request. The server records the set on the member's profile so the admin panel can
   * show which apps someone has signed in from. Omitted (e.g. in tests) means no header.
   */
  clientPlatform?: ClientPlatform;
  /**
   * Whether the device currently reads as online. Consulted only after a request fails at the
   * network level, to tell "you're offline" apart from "online, but this request failed in flight"
   * (ELARA-1146). React Native injects the NetInfo-backed connectivity port here. Omitted, the
   * transport falls back to `navigator.onLine` where the platform has it, and otherwise assumes
   * online — it never claims offline without a signal saying so.
   */
  isOnline?: () => boolean;
}

/** Per-flow transport behaviour taken from the flow's spec. */
export interface FlowCallableOptions {
  /** The spec's {@link AnyFlowSpec.idempotent}: allows one resend after a network-level failure. */
  idempotent?: boolean;
}

export function attachFlowSpec<const Spec extends AnyFlowSpec>(
  spec: Spec,
  opts?: HttpFlowTransportOptions,
): AttachedFlow<Spec> {
  const routerPath = isDebugFlowName(spec.name) ? '/api/flows/debug' : '/api/flows/app';
  return createFlowCallableProxy(routerPath, spec.name, opts, { idempotent: spec.idempotent }) as AttachedFlow<Spec>;
}

/**
 * Type for the proxy container - provides individual flow callables
 */
export type FlowProxyContainer<Flows extends Record<string, AnyFlowSpec>> = {
  [K in keyof Flows]: AttachedFlow<Flows[K]>;
};

/**
 * Creates a proxy that translates individual flow calls to router format.
 *
 * @param routerName - Logical router name ('appFlows' | 'debugFlows') or explicit router path.
 * @param flowSpecs - Map of client-side names to flow specs.
 * @param opts - HTTP transport options.
 */
export function createFlowProxy<Flows extends Record<string, AnyFlowSpec>>(
  routerName: string,
  flowSpecs: Flows,
  opts?: HttpFlowTransportOptions,
): FlowProxyContainer<Flows> {
  const routerPath = resolveRouterPath(routerName);
  const proxy = {} as FlowProxyContainer<Flows>;

  for (const [clientName, spec] of Object.entries(flowSpecs) as Array<[keyof Flows, AnyFlowSpec]>) {
    proxy[clientName] = createFlowCallableProxy(routerPath, spec.name, opts, {
      idempotent: spec.idempotent,
    }) as AttachedFlow<Flows[typeof clientName]>;
  }

  return proxy;
}

/**
 * Creates a callable proxy for a single flow that routes through an HTTP flow router.
 */
export function createFlowCallableProxy<Input, Output, Chunk>(
  routerPath: string,
  flowName: string,
  opts?: HttpFlowTransportOptions,
  flowOptions: FlowCallableOptions = {},
): FlowCallable<string, Input, Output, Chunk> {
  const fetchOptions: FlowFetchOptions = {
    flowName,
    isOnline: opts?.isOnline,
    retryOnNetworkFailure: flowOptions.idempotent === true,
  };

  const callable = async (input: Input, options?: FlowCallOptions): Promise<FlowResult<Output>> => {
    const endpoint = toAbsolutePath(routerPath, opts?.baseUrl);
    const payload = { [flowName]: normalizeFlowInput(input) } as Record<string, unknown>;
    const response = await performFlowFetch(
      opts?.fetchImpl,
      endpoint,
      {
        method: 'POST',
        headers: await buildJsonHeaders(opts),
        body: JSON.stringify(payload),
        signal: options?.signal,
        ...(options?.keepalive ? { keepalive: true } : {}),
      },
      fetchOptions,
    );

    const body = await parseJsonBody(response);
    if (!response.ok) {
      const flowError = toFlowHttpError(response.status, body);
      await maybeHandleUnauthenticatedError(flowError, opts);
      throw flowError;
    }

    const output = extractFlowOutput<Output>(flowName, body);
    return { data: output };
  };

  callable.stream = async (input: Input, options?: FlowStreamOptions): Promise<FlowStreamResult<Output, Chunk>> => {
    const endpoint = toAbsolutePath(routerPath, opts?.baseUrl);
    const payload = { [flowName]: normalizeFlowInput(input) } as Record<string, unknown>;
    const response = await performFlowFetch(
      opts?.fetchImpl,
      endpoint,
      {
        method: 'POST',
        headers: await buildSseHeaders(opts),
        body: JSON.stringify(payload),
        signal: options?.signal,
      },
      fetchOptions,
    );

    if (!response.ok) {
      const body = await parseJsonBody(response);
      const flowError = toFlowHttpError(response.status, body);
      await maybeHandleUnauthenticatedError(flowError, opts);
      throw flowError;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('text/event-stream')) {
      const rawFallbackBody = await response.text();
      const fallbackBody = rawFallbackBody.slice(0, 300);
      const receivedHtml = fallbackBody.trimStart().startsWith('<');
      const reason = receivedHtml
        ? 'received an HTML document instead of an SSE stream'
        : `received '${contentType || 'unknown'}' instead of 'text/event-stream'`;
      throw new FlowHttpError(`Server did not return a valid stream: ${reason}`, 'internal', response.status || 500, {
        contentType,
        preview: fallbackBody,
      });
    }

    if (!response.body) {
      throw new FlowHttpError(
        'Streaming response body is unavailable. The server returned an SSE content type but the fetch ' +
          'implementation did not expose a readable `response.body`. On React Native, inject a ' +
          'streaming-capable fetch (e.g. `expo/fetch`) via HttpFlowTransportOptions.fetchImpl.',
        'internal',
        response.status || 500,
      );
    }

    let resolveOutput: ((value: Output | PromiseLike<Output>) => void) | undefined;
    let rejectOutput: ((reason?: unknown) => void) | undefined;
    let sawResultEvent = false;
    const data = new Promise<Output>((resolve, reject) => {
      resolveOutput = resolve;
      rejectOutput = reject;
    });
    // Prevent unhandled rejections when callers only consume `stream`.
    void data.catch(() => undefined);

    const stream = (async function* (): AsyncGenerator<Chunk> {
      try {
        for await (const sseEvent of parseSse(response.body!, options?.signal)) {
          if (sseEvent.event === 'chunk') {
            const payload = parseEventData<RouterEvent<Chunk>>(sseEvent.data, 'chunk');
            if (payload.flow !== flowName) {
              continue;
            }
            if (payload.chunk !== undefined) {
              yield payload.chunk;
            }
            continue;
          }

          if (sseEvent.event === 'result') {
            const payload = parseEventData<RouterEvent<Chunk, Output>>(sseEvent.data, 'result');
            if (payload.flow !== flowName) {
              continue;
            }
            if (payload.output === undefined) {
              throw new Error(`Missing output payload for flow '${flowName}'`);
            }
            sawResultEvent = true;
            resolveOutput?.(payload.output);
            continue;
          }

          if (sseEvent.event === 'error') {
            const payload = parseEventData<RouterEvent>(sseEvent.data, 'error');
            if (payload.flow !== flowName) {
              continue;
            }
            throw new FlowHttpError(
              payload.error?.message || `Flow '${flowName}' failed`,
              payload.error?.code || 'internal',
              500,
              payload.error?.details,
            );
          }

          if (sseEvent.event === 'end') {
            break;
          }
        }
      } catch (error) {
        rejectOutput?.(error);
        throw error;
      }
    })();

    // Ensure the output promise does not hang if the stream ends without a result event.
    const guardedStream = (async function* (): AsyncGenerator<Chunk> {
      try {
        for await (const chunk of stream) {
          yield chunk;
        }
      } finally {
        if (!sawResultEvent) {
          rejectOutput?.(new Error(`Stream for flow '${flowName}' ended without a result event`));
        }
      }
    })();

    return { stream: guardedStream, data };
  };

  Object.defineProperty(callable, 'name', { value: flowName, configurable: true });
  return callable as FlowCallable<string, Input, Output, Chunk>;
}

function normalizeFlowInput<Input>(input: Input): Input | null {
  // JSON drops keys with undefined values; use null to preserve the requested flow key.
  return input === undefined ? null : input;
}

function resolveRouterPath(routerName: string): string {
  if (routerName.startsWith('/')) {
    return routerName;
  }

  if (routerName === 'appFlows') {
    return '/api/flows/app';
  }

  if (routerName === 'debugFlows') {
    return '/api/flows/debug';
  }

  const normalized = routerName.replace(/Flows$/, '').toLowerCase();
  return `/api/flows/${normalized}`;
}

function isDebugFlowName(flowName: string): boolean {
  return flowName.startsWith('Debug');
}

export function toAbsolutePath(path: string, baseUrl?: string): string {
  if (/^https?:\/\//.test(path)) {
    return path;
  }

  if (!baseUrl) {
    return path;
  }

  return new URL(path, baseUrl).toString();
}

async function buildJsonHeaders(opts?: HttpFlowTransportOptions): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'content-type': 'application/json', accept: 'application/json' };

  if (opts?.clientPlatform) {
    headers[CLIENT_PLATFORM_HEADER] = opts.clientPlatform;
  }

  const token = await opts?.getAuthToken?.();
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  return headers;
}

async function buildSseHeaders(opts?: HttpFlowTransportOptions): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'content-type': 'application/json', accept: 'text/event-stream' };

  if (opts?.clientPlatform) {
    headers[CLIENT_PLATFORM_HEADER] = opts.clientPlatform;
  }

  const token = await opts?.getAuthToken?.();
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  return headers;
}

async function parseJsonBody(response: Response): Promise<unknown> {
  const raw = await response.text();
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw) as unknown;
  } catch (error) {
    const contentType = response.headers.get('content-type') ?? '';
    const preview = raw.slice(0, 300);
    const receivedHtml = preview.trimStart().startsWith('<');
    const reason = receivedHtml
      ? 'received an HTML document instead of JSON'
      : `received '${contentType || 'unknown'}' with invalid JSON`;
    throw new FlowHttpError(`Failed to parse JSON response: ${reason}`, 'internal', response.status || 500, {
      parseError: errorMessage(error),
      contentType,
      preview,
    });
  }
}

function toFlowHttpError(status: number, body: unknown): FlowHttpError {
  const envelope = (body && typeof body === 'object' ? body : {}) as FlowErrorEnvelope;
  const code = envelope.error?.code || 'internal';
  const message = envelope.error?.message || `Request failed with status ${status}`;
  return new FlowHttpError(message, code, status, envelope.error?.details);
}

async function maybeHandleUnauthenticatedError(error: FlowHttpError, opts?: HttpFlowTransportOptions): Promise<void> {
  if (!(error.status === 401 || error.code === 'unauthenticated')) {
    return;
  }
  if (!opts?.onUnauthenticatedError) {
    return;
  }
  try {
    await opts.onUnauthenticatedError(error);
  } catch (hookError) {
    console.warn('[flow-utils] onUnauthenticatedError callback failed', hookError);
  }
}

function extractFlowOutput<T>(flowName: string, body: unknown): T {
  if (!body || typeof body !== 'object') {
    throw new Error(`Router did not return a valid payload for flow '${flowName}'`);
  }

  const payload = body as Record<string, unknown>;
  if (!(flowName in payload)) {
    throw new Error(`Router did not return output for flow '${flowName}'`);
  }

  return payload[flowName] as T;
}

function parseEventData<T>(rawData: string, eventName: string): T {
  if (!rawData) {
    throw new Error(`Received empty SSE data for event '${eventName}'`);
  }

  try {
    return JSON.parse(rawData) as T;
  } catch (error) {
    throw new Error(`Failed to parse SSE '${eventName}' payload: ${errorMessage(error)}`);
  }
}

interface ParsedSseEvent {
  event: string;
  data: string;
}

async function* parseSse(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<ParsedSseEvent, void, unknown> {
  const reader = body.getReader();
  const decoder = new (resolveTextDecoder())();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) {
        throw createAbortError();
      }

      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      while (true) {
        const separator = findSseSeparator(buffer);
        if (!separator) {
          break;
        }

        const chunk = buffer.slice(0, separator.index);
        buffer = buffer.slice(separator.index + separator.length);

        const parsed = parseSseChunk(chunk);
        if (parsed) {
          yield parsed;
        }
      }
    }

    buffer += decoder.decode();
    if (buffer.trim()) {
      const parsed = parseSseChunk(buffer);
      if (parsed) {
        yield parsed;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseChunk(chunk: string): ParsedSseEvent | undefined {
  const lines = chunk
    .split(/\r?\n/u)
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0 && !line.startsWith(':'));

  if (lines.length === 0) {
    return;
  }

  let event = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
      continue;
    }

    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  }

  return { event, data: dataLines.join('\n') };
}

/**
 * Platform seams (ELARA-688)
 *
 * The flow transport must run on both web and React Native. Web exposes `fetch`,
 * `DOMException`, and `TextDecoder` as globals; RN exposes a non-streaming `fetch` and may
 * lack `DOMException`/`TextDecoder` entirely until polyfilled. These helpers are the single
 * point where the transport touches those environment-specific primitives, so RN can inject
 * or polyfill them without forking the transport logic.
 */

/** How {@link performFlowFetch} classifies and recovers from a network-level failure. */
export interface FlowFetchOptions {
  /** Flow being called, recorded on a network failure's details for triage. */
  flowName?: string;
  /** See {@link HttpFlowTransportOptions.isOnline}. */
  isOnline?: () => boolean;
  /**
   * Resend once after a network-level failure while online. Only for idempotent flows: the failed
   * request may have reached the server, so a resend of anything else could run it twice.
   */
  retryOnNetworkFailure?: boolean;
}

/**
 * Pause before resending after a network-level failure. Long enough for the OS to retire the dead
 * connection the first attempt hit (so the resend dials a fresh one), short enough not to be felt.
 */
const NETWORK_RETRY_DELAY_MS = 300;

/**
 * Performs a flow request using the injected `fetchImpl`, or the global `fetch` otherwise.
 *
 * Rejections that mean "no response was produced" are normalised into a `FlowHttpError`:
 * {@link OFFLINE_FLOW_ERROR_CODE} when the device reads offline, {@link UNREACHABLE_FLOW_ERROR_CODE}
 * when it reads online. An idempotent request that fails while online is resent once first.
 */
export async function performFlowFetch(
  fetchImpl: typeof fetch | undefined,
  input: string,
  init: RequestInit,
  options: FlowFetchOptions = {},
): Promise<Response> {
  const dispatch = resolveFetch(fetchImpl);
  const isOnline = options.isOnline ?? readPlatformOnline;
  let attempts = 0;

  for (;;) {
    attempts += 1;
    try {
      return await dispatch(input, init);
    } catch (error) {
      // `fetch` rejects only when no response was produced at all: a dead network, a DNS failure, a
      // connection the OS dropped mid-request, or a caller-triggered abort. An abort is deliberate
      // and must stay recognisable by name, so it passes through untouched. Everything else is
      // normalised here — the single choke point for every flow request — because the raw rejection
      // is an untyped, platform-specific `TypeError` no call site can tell from a server failure.
      if (isAbortError(error)) throw error;

      // The rejection alone cannot say *why* nothing came back, so connectivity decides. Reading it
      // only now, after the failure, is deliberate: it is the reading at the moment that matters.
      // Labelling every rejection "offline" told online users they were offline whenever iOS
      // handed back a connection it had torn down during a suspend (ELARA-1146).
      const online = isOnline();
      if (online && options.retryOnNetworkFailure && attempts === 1) {
        await pauseBeforeRetry(init.signal);
        continue;
      }

      throw new FlowHttpError(
        online ? UNREACHABLE_FLOW_ERROR_MESSAGE : OFFLINE_FLOW_ERROR_MESSAGE,
        online ? UNREACHABLE_FLOW_ERROR_CODE : OFFLINE_FLOW_ERROR_CODE,
        0,
        { cause: errorMessage(error), url: input, flow: options.flowName, attempts },
      );
    }
  }
}

/**
 * Connectivity fallback when no `isOnline` port is injected (web, and the direct `performFlowFetch`
 * callers). `navigator.onLine === false` is a reliable "offline"; anything else — including a
 * platform with no `navigator.onLine`, like React Native — is treated as online, so the transport
 * never claims the guest is offline without a signal that says so.
 */
function readPlatformOnline(): boolean {
  const navigatorLike = (globalThis as { navigator?: { onLine?: unknown } }).navigator;
  return navigatorLike?.onLine !== false;
}

/** Waits {@link NETWORK_RETRY_DELAY_MS}, then honours an abort raised during the wait. */
async function pauseBeforeRetry(signal: AbortSignal | null | undefined): Promise<void> {
  await new Promise<void>((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
  if (signal?.aborted) throw createAbortError();
}

/**
 * Resolves the fetch used for a request. The global is resolved and invoked at request time —
 * never captured at proxy-creation time — so the transport always uses the current
 * `globalThis.fetch`, including any tracing instrumentation (e.g. Sentry) that wraps it after
 * bootstrap. It is bound to `globalThis` so the receiver is correct (browsers throw "Illegal
 * invocation" on a detached `fetch`) without persistently wrapping the global.
 */
function resolveFetch(fetchImpl: typeof fetch | undefined): typeof fetch {
  if (fetchImpl) return fetchImpl;

  const globalFetch = globalThis.fetch;
  if (!globalFetch) {
    throw new Error(
      'No fetch implementation available. Provide one via HttpFlowTransportOptions.fetchImpl ' +
        '(e.g. `expo/fetch` on React Native).',
    );
  }

  return (requestInput, requestInit) => globalFetch.call(globalThis, requestInput, requestInit);
}

/** Whether an error is a caller-triggered abort, which must not be reported as an offline failure. */
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Builds a portable `AbortError`. Prefers the web-standard `DOMException` when present and
 * falls back to a tagged `Error` so callers can still branch on `error.name === 'AbortError'`
 * in environments (like React Native) that lack `DOMException`.
 */
function createAbortError(): Error {
  if (typeof DOMException === 'function') {
    return new DOMException('The operation was aborted.', 'AbortError');
  }

  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
}

interface TextDecoderInstance {
  decode(input?: Uint8Array, options?: { stream?: boolean }): string;
}

interface TextDecoderCtor {
  new (): TextDecoderInstance;
}

/**
 * Resolves the `TextDecoder` constructor used to decode the SSE byte stream. This is the
 * single import point for `TextDecoder`: on RN a polyfill (e.g. `react-native-polyfill-globals`)
 * installs it on `globalThis` before the transport runs.
 */
function resolveTextDecoder(): TextDecoderCtor {
  const ctor = (globalThis as { TextDecoder?: TextDecoderCtor }).TextDecoder;
  if (!ctor) {
    throw new FlowHttpError(
      'No TextDecoder implementation available for SSE streaming. Ensure a TextDecoder polyfill ' +
        'is installed (e.g. `react-native-polyfill-globals` on React Native).',
      'internal',
      500,
    );
  }

  return ctor;
}

function findSseSeparator(buffer: string): { index: number; length: number } | undefined {
  const lfIndex = buffer.indexOf('\n\n');
  const crlfIndex = buffer.indexOf('\r\n\r\n');

  if (lfIndex === -1 && crlfIndex === -1) {
    return;
  }

  if (lfIndex === -1) {
    return { index: crlfIndex, length: 4 };
  }

  if (crlfIndex === -1) {
    return { index: lfIndex, length: 2 };
  }

  return lfIndex < crlfIndex ? { index: lfIndex, length: 2 } : { index: crlfIndex, length: 4 };
}
